import axios from "axios";
import moment from "moment";
import Cloner from "../../Utilities/Generic/Cloner";
import { formatDateTime } from "../../Utilities/Generic/Strings";
import { assembleReport, getFullReport, type ReportType } from "../../Utilities/Platform/Reports";
import { type DashboardReport } from "./Dashboard";

const UNFILTERED_CACHE_KEY = "_unfiltered_report";

export interface DashboardReportType extends ReportType {
    query: string;
    error: boolean;
}

export class DashboardManager {
    protected pending: Record<string, Record<string, Array<(report: DashboardReportType) => void>>> = {};
    protected cached: Record<string, Record<string, DashboardReportType>> = {};

    public preloadReport(user: string, report: DashboardReport, filterUser: boolean): void {
        void this.loadReport(user, report, filterUser);
    }

    public async loadReport(user: string, report: DashboardReport, filterUser: boolean): Promise<DashboardReportType> {
        const cacheKey = filterUser ? user : UNFILTERED_CACHE_KEY;

        // Check to see if the report is already loaded
        if(typeof this.cached[cacheKey] === "undefined") {
            this.cached[cacheKey] = {};
        }

        if(this.cached[cacheKey][report.id]) {
            return this.cached[cacheKey][report.id];
        }

        // Check to see if the report is being loaded
        if(typeof this.pending[cacheKey] === "undefined") {
            this.pending[cacheKey] = {};
        }

        if(this.pending[cacheKey][report.id]) {
            return new Promise((res) => {
                this.pending[cacheKey][report.id].push(res);
            });
        }

        // Prepare the pending array for the next calls
        this.pending[cacheKey][report.id] = [];

        const query = Cloner.Clone(report.report);

        if(user && filterUser) {
            if(report.type !== "purchase" && report.type !== "advanced") {
                query.where.push({
                    field   : "user_id",
                    operator: "=",
                    value   : `"${user}"`,
                    and     : true,
                });
            }
        }

        const assembledQuery = assembleReport(query);

        const cancelToken = axios.CancelToken.source();
        const reportData = await getFullReport(assembledQuery, {
            // NOTE: This is done so that the request looks the same when used in the Caching
            cancelToken         : cancelToken.token,
            ignoreDivisionByZero: true,
        }, true)
            .then(report => ({
                ...report,
                query: assembledQuery,
                error: false,
            } as DashboardReportType))
            .catch(err => {
                console.error("Failed to fetch report data", err);

                return {
                    key      : "",
                    completed: true,
                    query    : assembledQuery,
                    data     : {
                        columns: [],
                        totals : {},
                        range  : {
                            start: formatDateTime(moment()),
                            end  : formatDateTime(moment()),
                        },
                        groups        : [],
                        data          : [],
                        widest        : {},
                        order         : [],
                        nullGroupNames: [],
                    },
                    error: true,
                } as DashboardReportType;
            });

        // Set the cached
        this.cached[cacheKey][report.id] = reportData;

        // Clear all the pending
        for(const callback of this.pending[cacheKey][report.id]) {
            callback(reportData);
        }

        return reportData;
    }
}
